/**
 * Financial Accounts settings come in two flavours that share their screens:
 * the organization-wide page (`/settings/financial-accounts`) and one
 * facility's page (`/settings/facilities/:facilityId/financial-accounts`).
 * A facility scope narrows every list to the accounts assigned to that
 * facility; without one the screens behave as the organization-wide page.
 */

/** Where the organization-wide Financial Accounts settings live. */
export const ORG_FINANCIAL_ACCOUNTS_PATH = '/settings/financial-accounts'

/** One facility's Financial Accounts page and everything below it. */
export interface FacilityFinancialAccountsScope {
  facilityId: string
  facilityName: string
  /**
   * The facility is inactive: its records are shown but nothing can be
   * changed (an explicit exception to ADR 0016, which otherwise hides them).
   */
  readOnly: boolean
}

/** Copy of the facility-scoped pages. */
export const FACILITY_SCOPE_COPY = {
  subtitle: 'Connections to financial institutions and manual financial accounts for this facility',
  crumbSuffix: 'Financial Accounts',
  inactiveBanner:
    'This facility is inactive. Financial accounts are shown in read-only mode. Reactivate the facility to manage its connections, accounts and cards.',
  /** Tooltip on every control disabled because the facility is inactive. */
  readOnlyTooltip: 'Not available for an inactive facility',
  notFound: 'This facility no longer exists.',
} as const

/** The facility page's own title in breadcrumbs and back links: "{Facility} Financial Accounts". */
export function facilityFinancialAccountsLabel(facilityName: string): string {
  return `${facilityName} ${FACILITY_SCOPE_COPY.crumbSuffix}`
}

/**
 * Empty text of a connection's accounts table on a facility page with no
 * search or filter: the institution may well have accounts, just none here.
 */
export function facilityAccountsEmpty(facilityName: string): string {
  return `No accounts or cards of this institution are assigned to ${facilityName}.`
}

/**
 * Root path of the Financial Accounts screens for a scope: the facility's page
 * when scoped, the organization-wide page otherwise. Detail pages hang below
 * it (`/connections/:id`, `/manual/:name`, `/manual-unnamed`).
 */
export function financialAccountsBasePath(scope?: Pick<FacilityFinancialAccountsScope, 'facilityId'> | null): string {
  return scope
    ? `/settings/facilities/${encodeURIComponent(scope.facilityId)}/financial-accounts`
    : ORG_FINANCIAL_ACCOUNTS_PATH
}
